import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext.js';
import { API_BASE } from '../config.js';

interface WritingSettings {
  replaceHyphensWithDashes: boolean;
  collapseConsecutiveSpaces: boolean;
  enableLists: boolean;
  enableMentions: boolean;
  enableScriptMode: boolean;
  automaticReplacements: [string, string][];
  textJustification: 'left' | 'center' | 'right' | 'justified' | 'none';
}

interface UserSettings {
  email_notifications_disabled: boolean;
}

interface SettingsContextType {
  writingSettings: WritingSettings;
  settings: UserSettings;
  loading: boolean;
  updateWritingSetting: <K extends keyof WritingSettings>(key: K, value: WritingSettings[K]) => Promise<void>;
  updateSetting: <K extends keyof UserSettings>(key: K, value: UserSettings[K]) => Promise<void>;
  fetchHierarchicalWritingSettings: (projectId: string | null, documentId: string | null) => Promise<void>;
  fetchSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const defaultWritingSettings: WritingSettings = {
  replaceHyphensWithDashes: true,
  collapseConsecutiveSpaces: true,
  enableLists: false,
  enableMentions: false,
  enableScriptMode: false,
  automaticReplacements: [['<<', '«'], ['>>', '»'], ['->', '→'], ['<-', '←']],
  textJustification: 'none',
};

const defaultUserSettings: UserSettings = {
  email_notifications_disabled: false,
};

function hasHierarchicalWritingSettingsScope(
  projectId: string | null,
  documentId: string | null,
): boolean {
  return Boolean(
    (documentId && documentId !== 'new') || (projectId && projectId !== 'new'),
  );
}

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { token, user } = useAuth();
  const [writingSettings, setWritingSettings] = useState<WritingSettings>(defaultWritingSettings);
  const [settings, setSettings] = useState<UserSettings>(defaultUserSettings);
  const [loading, setLoading] = useState<boolean>(true);
  const hierarchicalWritingSettingsScopeRef = useRef(false);
  const hierarchicalWritingSettingsFetchGenerationRef = useRef(0);

  const fetchSettings = useCallback(async (): Promise<void> => {
    if (!token || !user) {
      if (!hierarchicalWritingSettingsScopeRef.current) {
        setWritingSettings(defaultWritingSettings);
      }
      setSettings(defaultUserSettings);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/settings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (!hierarchicalWritingSettingsScopeRef.current) {
          setWritingSettings(data.writingSettings || defaultWritingSettings);
        }
        setSettings(data.settings || defaultUserSettings);
      } else {
        if (!hierarchicalWritingSettingsScopeRef.current) {
          setWritingSettings(defaultWritingSettings);
        }
        setSettings(defaultUserSettings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      if (!hierarchicalWritingSettingsScopeRef.current) {
        setWritingSettings(defaultWritingSettings);
      }
      setSettings(defaultUserSettings);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  const fetchHierarchicalWritingSettings = useCallback(async (projectId: string | null, documentId: string | null): Promise<void> => {
    hierarchicalWritingSettingsScopeRef.current = hasHierarchicalWritingSettingsScope(projectId, documentId);
    const fetchGeneration = ++hierarchicalWritingSettingsFetchGenerationRef.current;

    if (!token || !user) {
      if (fetchGeneration === hierarchicalWritingSettingsFetchGenerationRef.current) {
        setWritingSettings(defaultWritingSettings);
      }
      return;
    }

    try {
      let url = `${API_BASE}/api/settings/writing`;
      if (documentId && documentId !== 'new') {
        url = `${API_BASE}/api/documents/${documentId}/writing-settings`;
      } else if (projectId && projectId !== 'new') {
        url = `${API_BASE}/api/projects/${projectId}/writing-settings`;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (fetchGeneration !== hierarchicalWritingSettingsFetchGenerationRef.current) {
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setWritingSettings(data.writingSettings || defaultWritingSettings);
      } else {
        setWritingSettings(defaultWritingSettings);
      }
    } catch (error) {
      console.error('Error fetching hierarchical writing settings:', error);
      if (fetchGeneration === hierarchicalWritingSettingsFetchGenerationRef.current) {
        setWritingSettings(defaultWritingSettings);
      }
    }
  }, [token, user]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).writingSettings = writingSettings;
      (window as any).getWritingSettings = async () => {
        const documentId = window.location.pathname.match(/\/documents\/([^\/]+)/)?.[1];
        const projectId = window.location.pathname.match(/\/projects\/([^\/]+)/)?.[1];
        
        if (!token) {
          return { error: 'Not authenticated' };
        }

        try {
          let url = `${API_BASE}/api/settings/writing`;
          if (documentId && documentId !== 'new') {
            url = `${API_BASE}/api/documents/${documentId}/writing-settings`;
          } else if (projectId && projectId !== 'new') {
            url = `${API_BASE}/api/projects/${projectId}/writing-settings`;
          }

          const response = await fetch(url, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            return {
              writingSettings: data.writingSettings,
              source: data.source,
              documentWritingSettings: data.documentWritingSettings,
              projectWritingSettings: data.projectWritingSettings,
            };
          } else {
            return { error: 'Failed to fetch settings' };
          }
        } catch (error) {
          return { error: String(error) };
        }
      };
    }
  }, [writingSettings, token]);

  const updateWritingSetting = useCallback(async <K extends keyof WritingSettings>(
    key: K,
    value: WritingSettings[K]
  ): Promise<void> => {
    if (!token || !user) {
      setWritingSettings((prev) => ({ ...prev, [key]: value }));
      return;
    }

    const newSettings = { ...writingSettings, [key]: value };
    if (!hierarchicalWritingSettingsScopeRef.current) {
      setWritingSettings(newSettings);
    }

    try {
      const response = await fetch(`${API_BASE}/api/settings/writing`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ [key]: value }),
      });

      if (response.ok) {
        const data = await response.json();
        if (!hierarchicalWritingSettingsScopeRef.current) {
          setWritingSettings(data.writingSettings);
        }
        window.dispatchEvent(new CustomEvent('userWritingSettingsUpdated'));
      } else {
        await fetchSettings();
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      await fetchSettings();
    }
  }, [token, user, writingSettings, fetchSettings]);

  const updateSetting = useCallback(async <K extends keyof UserSettings>(
    key: K,
    value: UserSettings[K]
  ): Promise<void> => {
    if (!token || !user) {
      setSettings((prev) => ({ ...prev, [key]: value }));
      return;
    }

    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);

    try {
      const response = await fetch(`${API_BASE}/api/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ [key]: value }),
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
      } else {
        await fetchSettings();
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      await fetchSettings();
    }
  }, [token, user, settings, fetchSettings]);

  return (
    <SettingsContext.Provider value={{ writingSettings, settings, loading, updateWritingSetting, updateSetting, fetchHierarchicalWritingSettings, fetchSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
